import React, { useMemo, useState } from "react";

const TYPE_META = {
  correct:   { label: "Correct",   icon: "✅", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  wrong:     { label: "Wrong",     icon: "❌", color: "text-red-400",     bg: "bg-red-500/10" },
  duplicate: { label: "Duplicate", icon: "🔄", color: "text-orange-400",  bg: "bg-orange-500/10" },
  miss:      { label: "Missed",    icon: "💨", color: "text-rose-400",    bg: "bg-rose-500/10" },
  freeze:    { label: "Freeze",    icon: "❄️", color: "text-sky-400",     bg: "bg-sky-500/10" },
  bonus:     { label: "Bonus",     icon: "⭐", color: "text-yellow-400",  bg: "bg-yellow-500/10" },
};

export default function ScoreLedger({ scoreLedger = [], finalScore = 0 }) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const stats = useMemo(() => {
    const earned = scoreLedger
      .filter((x) => x.points > 0)
      .reduce((a, b) => a + b.points, 0);

    const deducted = Math.abs(
      scoreLedger
        .filter((x) => x.points < 0)
        .reduce((a, b) => a + b.points, 0)
    );

    // Group by type for summary chips
    const byType = {};
    scoreLedger.forEach((item) => {
      if (!byType[item.type]) byType[item.type] = { count: 0, points: 0 };
      byType[item.type].count += 1;
      byType[item.type].points += item.points;
    });

    return { earned, deducted, byType };
  }, [scoreLedger]);

  // Show only last 8 events by default
  const visibleEvents = showAll ? scoreLedger : scoreLedger.slice(-8);

  return (
    <div className="w-full max-w-md mx-auto px-3">
      {/* Toggle Button */}
      <div className="flex justify-center">
        <button
          onClick={() => setOpen((p) => !p)}
          className="px-4 py-2 rounded-full text-sm font-semibold
                     bg-slate-800/80 hover:bg-slate-700 border border-slate-600
                     text-cyan-300 transition-all active:scale-95"
        >
          {open ? "▲ Hide Score Breakdown" : "📜 Score Breakdown"}
        </button>
      </div>

      {open && (
        <div className="mt-3 rounded-2xl border border-slate-700/80 bg-slate-900/90 backdrop-blur-sm overflow-hidden shadow-xl">
          
          {/* ── Summary Header ── */}
          <div className="p-4 bg-gradient-to-b from-slate-800/80 to-transparent">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 py-2">
                <div className="text-[10px] uppercase tracking-wider text-emerald-400/80">Earned</div>
                <div className="text-lg font-bold text-emerald-400">+{stats.earned}</div>
              </div>
              <div className="rounded-xl bg-red-500/10 border border-red-500/20 py-2">
                <div className="text-[10px] uppercase tracking-wider text-red-400/80">Deducted</div>
                <div className="text-lg font-bold text-red-400">-{stats.deducted}</div>
              </div>
              <div className="rounded-xl bg-cyan-500/10 border border-cyan-500/20 py-2">
                <div className="text-[10px] uppercase tracking-wider text-cyan-400/80">Final</div>
                <div className="text-lg font-bold text-cyan-300">{finalScore}</div>
              </div>
            </div>

            {/* Type chips */}
            {Object.keys(stats.byType).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3 justify-center">
                {Object.entries(stats.byType).map(([type, data]) => {
                  const meta = TYPE_META[type] || { icon: "•", color: "text-slate-400", bg: "bg-slate-700/50" };
                  return (
                    <div
                      key={type}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${meta.bg} ${meta.color}`}
                    >
                      <span>{meta.icon}</span>
                      <span>{data.count}×</span>
                      <span className="opacity-70">
                        {data.points > 0 ? `+${data.points}` : data.points}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Event List ── */}
          <div className="max-h-64 overflow-y-auto">
            {scoreLedger.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-sm">
                No score events yet
              </div>
            ) : (
              <div className="divide-y divide-slate-800/70">
                {visibleEvents.map((item, i) => {
                  const meta = TYPE_META[item.type] || {
                    icon: "•",
                    color: "text-slate-400",
                    bg: "bg-slate-800",
                  };

                  return (
                    <div
                      key={i}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800/40 transition-colors"
                    >
                      {/* Icon */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base ${meta.bg}`}>
                        {meta.icon}
                      </div>

                      {/* Reason */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">
                          {item.reason || meta.label}
                        </div>
                        <div className="text-[11px] text-slate-500 capitalize">
                          {meta.label}
                        </div>
                      </div>

                      {/* Points */}
                      <div
                        className={`font-bold text-sm tabular-nums ${
                          item.points >= 0 ? "text-emerald-400" : "text-red-400"
                        }`}
                      >
                        {item.points > 0 ? "+" : ""}
                        {item.points}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Show more / less ── */}
          {scoreLedger.length > 8 && (
            <button
              onClick={() => setShowAll((p) => !p)}
              className="w-full py-2.5 text-xs font-medium text-slate-400 hover:text-cyan-300 
                         border-t border-slate-800 hover:bg-slate-800/50 transition-colors"
            >
              {showAll
                ? "Show less"
                : `Show all ${scoreLedger.length} events`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}